// Which sign-in buttons the front door shows, per environment.
//
//   VITE_IDENTITY_PROVIDERS=google,github   production, staging, local dev
//   (unset)                                 Google only
//
// Every environment offers both. GitHub cannot yet produce a usable STAFF
// account for anybody whose GitHub primary address is not @heavybit.com: the
// exchange refuses with `identity_not_linked` rather than create a second
// staff identity, and account linking is a separate, unbuilt feature. A staff
// member who clicks GitHub by mistake sees that refusal (or lands in a Network
// account, whose page names the signed-in address) and signs in with Google.
// Everybody else, Crucible's people above all, gets a working account.
//
// The list is one value for the whole page. An earlier rule added GitHub only
// for arrivals from Crucible's origin (VITE_IDENTITY_GITHUB_ORIGINS), keyed on
// return_to. An agent's consent flow reaches this page with a RELATIVE
// return_to (/identity/authorize?...), so that rule never fired there and a
// GitHub-only Crucible account could not connect its agent.
//
// The variable must never be REQUIRED. A missing or misspelt value falls back
// to Google with a console warning rather than throwing: a config typo that
// takes down the sign-in page is worse than one that shows one fewer button,
// and the warning is what stops the fallback from being silent.

export const IDENTITY_PROVIDERS = ["google", "github"] as const
export type OAuthProvider = (typeof IDENTITY_PROVIDERS)[number]

/** Google alone — what an unset or unusable value means. */
export const DEFAULT_IDENTITY_PROVIDERS: OAuthProvider[] = ["google"]

function isProvider(value: string): value is OAuthProvider {
  return (IDENTITY_PROVIDERS as readonly string[]).includes(value)
}

export function parseIdentityProviders(value: string | undefined): OAuthProvider[] {
  const requested = (value ?? "")
    .split(",")
    .map((name) => name.trim().toLowerCase())
    .filter(Boolean)
  if (requested.length === 0) return [...DEFAULT_IDENTITY_PROVIDERS]

  const unknown = requested.filter((name) => !isProvider(name))
  if (unknown.length > 0) {
    console.warn(
      `[config] VITE_IDENTITY_PROVIDERS lists unknown provider(s): ${unknown.join(", ")}. ` +
        `Known providers are ${IDENTITY_PROVIDERS.join(", ")}.`
    )
  }
  // Keep the order the environment asked for, without duplicates.
  const known = [...new Set(requested.filter(isProvider))]
  if (known.length === 0) {
    console.warn(
      "[config] VITE_IDENTITY_PROVIDERS named no known provider; falling back to Google."
    )
    return [...DEFAULT_IDENTITY_PROVIDERS]
  }
  return known
}

/** Button label and icon per provider. Brand names, not Heavybit's voice.
 *
 *  `label` is what you SEE and is deliberately the bare brand name: the
 *  buttons sit inline in one row inside a 26rem card, and "Continue with
 *  GitHub" beside "Continue with Google" cannot fit on one line there — they
 *  wrap back into the stacked full-width layout this replaced.
 *
 *  `action` is what a screen reader HEARS, because "Google, button" does not
 *  say what pressing it does. The card is still named "Sign in" (a visually
 *  hidden h1 — IdentityFrame's showTitle), so the visible text is not the
 *  only thing carrying the meaning, but the button should stand on its own. */
export const PROVIDER_PRESENTATION: Record<
  OAuthProvider,
  { label: string; action: string; icon: string }
> = {
  google: { label: "Google", action: "Sign in with Google", icon: "google" },
  github: { label: "GitHub", action: "Sign in with GitHub", icon: "github" },
}
